import moment from 'moment';
import { generateTimestampsInSeconds, toHHMMSS, formatDateTime } from '@/util/date';

/**
 * Transform RPM type label for display purposes.
 * If there's only 1 RPM type and it's 'CENTER', display as 'MAIN' instead.
 * Data mapping remains unchanged (still uses 'CENTER' internally).
 *
 * @param {string} rpmType - The RPM type (PORT, STARBOARD, CENTER)
 * @param {string[]} allRpmTypes - Array of all available RPM types
 * @returns {string} - Display label for the RPM type
 */
export function getRpmTypeLabel(rpmType, allRpmTypes = []) {
  // If only one RPM type exists and it's CENTER, display as MAIN
  if (allRpmTypes.length === 1 && rpmType === 'CENTER') {
    return 'MAIN';
  }
  return rpmType;
}

export function getIntervalSeconds(interval) {
  switch (interval) {
    case 'minute':
      return 60;
    case 'hour':
      return 60 * 60;
    case 'day':
      return 60 * 60 * 24;
    case 'week':
      return 60 * 60 * 24 * 7;
    default:
      return 60;
  }
}

function roundKeyByInterval(key, interval) {
  return moment(key * 1000).startOf(interval).unix();
}

function roundKeyLegacyHour(key, idx, interval) {
  let roundedKey = Math.round(key / 3600) * 3600;
  if (idx !== 0) roundedKey = moment(key * 1000).startOf(interval).unix();
  return roundedKey;
}

function buildRoundedMap(data, interval, roundingStrategy) {
  const rounded = {};
  Object.keys(data || {}).forEach((key, idx) => {
    const roundedKey = roundingStrategy === 'legacy-hour'
      ? roundKeyLegacyHour(parseInt(key, 10), idx, interval)
      : roundKeyByInterval(parseInt(key, 10), interval);
    rounded[roundedKey] = data[key];
  });
  return rounded;
}

export function buildFmsSeries({
  // deviceStatus = {},
  summary,
  dateRange,
  interval,
  roundingStrategy = 'interval',
  normalizeHourlyTimestamps = false,
  dataSort,
  intervalSecondsOverride,
}) {
  if (!summary) return [];

  const intervalSeconds = intervalSecondsOverride ?? getIntervalSeconds(interval);
  let timestamps = generateTimestampsInSeconds(
    moment(dateRange.start).unix(),
    moment(dateRange.end).unix(),
    intervalSeconds,
  );

  if (normalizeHourlyTimestamps) {
    timestamps = timestamps.map((timestamp, i) => {
      if (i === 0) return timestamp;
      return moment(timestamp * 1000).startOf('hour').unix();
    });
  }

  const roundedGps = buildRoundedMap(summary.gps?.data || {}, interval, roundingStrategy);
  const roundedRpm = buildRoundedMap(summary.rpm?.data || {}, interval, roundingStrategy);
  const roundedFm = buildRoundedMap(summary.flowmeter?.data || {}, interval, roundingStrategy);
  const roundedAe = buildRoundedMap(summary.ae?.data || {}, interval, roundingStrategy);
  const roundedTemperature = buildRoundedMap(summary.temperature?.data || {}, interval, roundingStrategy);
  const roundedPowermeter = buildRoundedMap(summary.powermeter?.data || {}, interval, roundingStrategy);

  let mappedSeries = timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {
      STARBOARD: {},
      PORT: {},
      CENTER: {},
    };
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const entry = {
      timestamp,
      gpsData,
      rpmData,
      fmData,
      aeData,
    };

    entry.temperatureData = roundedTemperature?.[timestamp] || {
      PORT: {},
      STARBOARD: {},
      CENTER: {},
    };

    entry.powermeterData = roundedPowermeter?.[timestamp] || {};

    return entry;
  });

  if (dataSort === 'latest') {
    mappedSeries = mappedSeries.sort((a, b) => b.timestamp - a.timestamp);
  }

  if (dataSort === 'oldest') {
    mappedSeries = mappedSeries.sort((a, b) => a.timestamp - b.timestamp);
  }

  return mappedSeries;
}

export function buildFmsSummary({
  deviceStatus = {},
  summary,
  dateRange,
  interval,
  aeFuelFixed = false,
  seriesRoundingStrategy = 'interval',
}) {
  if (!summary) return null;

  console.log('[buildFmsSummary] === START ===');
  console.log('[buildFmsSummary] Input summary keys:', Object.keys(summary));
  console.log('[buildFmsSummary] summary.flowmeter:', summary.flowmeter);
  console.log('[buildFmsSummary] summary.ae:', summary.ae);
  console.log('[buildFmsSummary] summary.gps:', summary.gps);
  console.log('[buildFmsSummary] deviceStatus:', deviceStatus);

  const mappedSummary = {};
  const intervalSeconds = getIntervalSeconds(interval);
  const unixRangeStart = moment(dateRange.start).unix();
  const unixRangeEnd = moment(dateRange.end).unix();
  const intervalCount = Math.ceil((unixRangeEnd - unixRangeStart) / intervalSeconds);

  console.log('[buildFmsSummary] intervalSeconds:', intervalSeconds);
  console.log('[buildFmsSummary] intervalCount:', intervalCount);
  console.log('[buildFmsSummary] dateRange:', { start: dateRange.start, end: dateRange.end });

  const lastGpsData = Object.values(summary.gps?.data || [{}])[Object.values(summary.gps?.data || [{}]).length - 1];
  const gpsCount = Object.values(summary.gps?.data || []).length;

  mappedSummary.gps = {
    lastGpsData,
    averageSpeed: (summary.gps?.summary?.averageSpeed * 0.5399568034557236).toFixed(2),
    totalDistance: (summary.gps?.summary?.totalDistance / 1852).toFixed(2),
    lastData: lastGpsData ? formatDateTime(lastGpsData.timestamp * 1000, 'DD MMMM YYYY HH:mm') : '-',
    percentage: ((gpsCount / intervalCount) * 100).toFixed(1),
  };

  const rpmCount = Object.values(summary.rpm?.data || []).length;
  const rpmTypes = deviceStatus?.rpmTypes || [];
  mappedSummary.rpm = {};

  rpmTypes.forEach((type) => {
    mappedSummary.rpm[type] = {
      lastData: Object.values(summary.rpm?.data || []).length > 0
        ? formatDateTime(
          Object.values(summary.rpm?.data)[Object.values(summary.rpm?.data).length - 1][type]?.timestamp * 1000,
          'DD MMMM YYYY HH:mm',
        )
        : '-',
      runningTime: toHHMMSS(summary.rpm?.summary?.runningTime?.[type] || 0),
      average: (summary.rpm?.summary?.average?.[type] || 0).toFixed(2),
      lastRpmData: Object.values(summary.rpm?.data || []).length > 0
        ? Object.values(summary.rpm?.data)[Object.values(summary.rpm?.data).length - 1][type]
        : {},
    };
  });

  mappedSummary.rpm.percentage = ((rpmCount / intervalCount) * 100).toFixed(1);

  const fmSummary = summary.flowmeter?.summary;
  const fm = {};

  console.log('[buildFmsSummary] fmSummary:', fmSummary);
  console.log('[buildFmsSummary] flowmeterPositions:', deviceStatus?.flowmeterPositions);

  if (fmSummary) {
    const positions = deviceStatus?.flowmeterPositions;
    console.log('[buildFmsSummary] Processing positions:', positions);

    positions.forEach((pos) => {
      const inKey = `${pos}_IN`;
      const outKey = `${pos}_OUT`;
      const consKey = `${pos.toLowerCase()}EngineCons`;
      const avgFlowKey = `${pos.toLowerCase()}AverageFlow`;

      console.log('[buildFmsSummary] Processing position:', pos, {
        inKey, outKey, consKey, avgFlowKey,
      });
      console.log('[buildFmsSummary] fmSummary[inKey]:', fmSummary[inKey]);
      console.log('[buildFmsSummary] fmSummary[consKey]:', fmSummary[consKey]);

      if (fmSummary[inKey] || fmSummary[consKey] || fmSummary[avgFlowKey]) {
        fm[pos] = {
          in: {
            avgFuelFlow: (fmSummary[inKey]?._averageFlowrate || 0).toFixed(2),
            engineCons: (fmSummary[inKey]?._totalVolumeFlow || 0).toFixed(2),
          },
        };
        console.log('[buildFmsSummary] fm[pos].in:', fm[pos].in);
      }

      if (fmSummary[outKey]) {
        fm[pos].out = {
          avgFuelFlow: (fmSummary[outKey]._averageFlowrate || 0).toFixed(2),
          engineCons: (fmSummary[outKey]._totalVolumeFlow || 0).toFixed(2),
        };
        console.log('[buildFmsSummary] fm[pos].out:', fm[pos].out);
      }
    });

    fm.meEngineCons = (fmSummary.meEngineCons || 0).toFixed(2);
    console.log('[buildFmsSummary] fm.meEngineCons:', fm.meEngineCons);
  }

  const fmCount = Object.values(summary?.flowmeter?.data || []).length;
  fm.percentage = ((fmCount / intervalCount) * 100).toFixed(1);

  mappedSummary.fm = fm;

  const lastAeData = Object.values(summary?.ae?.data || []).length > 0
    ? Object.values(summary?.ae?.data)[Object.values(summary.ae?.data).length - 1]
    : null;
  const lastAeKey = Object.keys(summary?.ae?.data || []).length > 0
    ? Object.keys(summary?.ae?.data)[Object.keys(summary.ae?.data || [{}]).length - 1]
    : null;

  const aeCount = deviceStatus?.aeCount || 0;
  const formatAeFuel = (value) => (aeFuelFixed ? (value || 0).toFixed(2) : (value || '-'));

  const aeData = {};
  let totalRunningSeconds = 0;
  let totalFuelConsumption = 0;

  console.log('[buildFmsSummary] aeCount:', aeCount);
  console.log('[buildFmsSummary] summary.ae.summary:', summary.ae?.summary);

  for (let i = 1; i <= aeCount; i += 1) {
    const aeKey = `AE${i}`;
    const aeNum = `ae${i}`;
    const fuelConsumption = summary.ae?.summary?.fuelConsumption?.[aeKey] || 0;
    const runningSeconds = summary.ae?.summary?.runningSeconds?.[aeKey] || 0;

    console.log(`[buildFmsSummary] AE${i}:`, { fuelConsumption, runningSeconds });

    aeData[aeNum] = {
      aeNumber: i,
      fuelConsumption: formatAeFuel(fuelConsumption),
      runningSeconds: toHHMMSS(runningSeconds),
      lastRunningTime: lastAeData?.[aeKey]?.runningTime || 0,
    };

    totalRunningSeconds += runningSeconds;
    totalFuelConsumption += fuelConsumption;
  }

  console.log('[buildFmsSummary] totalFuelConsumption:', totalFuelConsumption);
  console.log('[buildFmsSummary] totalRunningSeconds:', totalRunningSeconds);

  mappedSummary.ae = {
    ...aeData,
    lastAeData,
    lastData: lastAeKey ? formatDateTime(lastAeKey * 1000, 'DD MMMM YYYY HH:mm') : '-',
    percentage: ((aeCount / intervalCount) * 100).toFixed(1),
    runningSeconds: toHHMMSS(totalRunningSeconds),
    fuelConsumption: totalFuelConsumption.toFixed(2),
  };

  mappedSummary.temperature = {
    exhaust: {
      port: {
        average: summary?.temperature?.summary?.tempDetails?.EXHAUST_TEMPERATURE?.PORT?.[0]?.avg?.toFixed(2) || '-',
      },
      starboard: {
        average: summary?.temperature?.summary?.tempDetails?.EXHAUST_TEMPERATURE?.STARBOARD?.[0]?.avg?.toFixed(2) || '-',
      },
      center: {
        average: summary?.temperature?.summary?.tempDetails?.EXHAUST_TEMPERATURE?.CENTER?.[0]?.avg?.toFixed(2) || '-',
      },
    },
    tank: {
      port: {
        average: summary?.temperature?.summary?.tempDetails?.TEMPERATURE?.PORT?.[0]?.avg?.toFixed(2) || '-',
      },
      starboard: {
        average: summary?.temperature?.summary?.tempDetails?.TEMPERATURE?.STARBOARD?.[0]?.avg?.toFixed(2) || '-',
      },
      center: {
        average: summary?.temperature?.summary?.tempDetails?.TEMPERATURE?.CENTER?.[0]?.avg?.toFixed(2) || '-',
      },
    },
  };

  mappedSummary.powermeter = [];
  if (summary?.powermeter?.summary?.devices) {
    const pmDevices = Object.keys(summary.powermeter.summary.devices);
    pmDevices.forEach((pmDeviceKey) => {
      const pmData = summary.powermeter.summary.devices[pmDeviceKey]?.[0] || {};
      mappedSummary.powermeter.push({
        name: pmData.devcName || pmDeviceKey.replace('PM_', '').replace('_', ' '),
        totalEnergy: (pmData.totalEnergy || 0).toFixed(2),
        avgActivePower: (pmData.avgActivePower || 0).toFixed(2),
        avgFrequency: (pmData.avgFrequency || 0).toFixed(2),
        avgVoltage: (pmData.avgVoltageLtoN || 0).toFixed(2),
        avgCurrent: (pmData.avgCurrent || 0).toFixed(2),
        avgPowerFactor: (pmData.avgPowerFactor || 0).toFixed(2),
      });
    });
  }

  mappedSummary.series = buildFmsSeries({
    summary,
    dateRange,
    interval,
    roundingStrategy: seriesRoundingStrategy,
    deviceStatus,
  });

  console.log('[buildFmsSummary] === END ===');
  console.log('[buildFmsSummary] Final mappedSummary.fm:', mappedSummary.fm);
  console.log('[buildFmsSummary] Final mappedSummary.ae:', mappedSummary.ae);

  return mappedSummary;
}
